import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { logError } from '../../services/services';
import { isMobileSizeScreen, prefixPrevProps } from '../../utils/utilsGlobal';
import styles from './Tooltip.scss';

export class Tooltip extends Component {

  DEFAULT_DELAY = 300;
  DEFAULT_ALIGN = 'center';
  DEFAULT_PLACEMENT = 'top';

  targetRef = React.createRef();

  /** @type HTMLElement */ target = null;
  /** @type HTMLElement */ tooltip = null;
  /** @type HTMLElement */ scrollableParent = null;

  isMouseOverTooltip = false;
  mouseOverTooltipTimeout = null;
  showTimeout = null;
  hideTimeout = null;

  componentWillUnmount(){
    if (this.scrollableParent && this.tooltip) {
      this.scrollableParent.removeChild(this.tooltip);
    }
    clearTimeout(this.mouseOverTooltipTimeout);
    clearTimeout(this.showTimeout);
    clearTimeout(this.hideTimeout);
  };

  componentDidUpdate(prevProps){
    const { title, dangerouslySetInnerHTML, placement, align } = this.serializeTooltipParams(this.props);
    const {
      prevTitle,
      prevDangerouslySetInnerHTML,
      prevPlacement,
      prevAlign,
    } = prefixPrevProps(this.serializeTooltipParams(prevProps));

    if (this.tooltip && (title || dangerouslySetInnerHTML.__html)) {
      if (title !== prevTitle || dangerouslySetInnerHTML !== prevDangerouslySetInnerHTML) {
        this.setTooltipContent(title, dangerouslySetInnerHTML);
      }
      if (align !== prevAlign || placement !== prevPlacement) {
        this.setTooltipPosition(align, placement);
      }
    }
  }

  initialize = () => {
    const { title, dangerouslySetInnerHTML, align, placement } = this.serializeTooltipParams(this.props);

    if (title || dangerouslySetInnerHTML.__html) {
      this.target = this.getTarget();
      this.scrollableParent = this.getScrollableParent(this.target);
      this.tooltip = this.createTooltip(this.target, this.scrollableParent);

      this.setTooltipContent(title, dangerouslySetInnerHTML);
      this.setTooltipPosition(align, placement);
    }
  };

  getTarget = () => {
    return ReactDOM.findDOMNode(this.targetRef.current);
  };

  getScrollableParent = (target) => {
    const isElement = target instanceof HTMLElement;
    if (!isElement) {
      return null;
    }
    const { scrollHeight, clientHeight, parentNode } = target;
    const overflowY = window.getComputedStyle(target).overflowY;
    const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
    return isScrollable && scrollHeight >= clientHeight
      ? target
      : this.getScrollableParent(target.parentNode) || document.body;
  };

  createTooltip = (target, scrollableParent) => {
    const { className } = this.props;
    const tooltip = document.createElement('div');
    tooltip.classList.add(className, styles.tooltip);

    tooltip.addEventListener('mouseenter', this.onTooltipMouseOver);
    tooltip.addEventListener('mouseleave', this.hide);

    scrollableParent.appendChild(tooltip);
    return tooltip;
  };

  serializeTooltipParams = (params) => {
    const { title, dangerouslySetInnerHTML, align, placement, delay } = params;
    return {
      align: align || this.DEFAULT_ALIGN,
      delay: delay || this.DEFAULT_DELAY,
      placement: placement || this.DEFAULT_PLACEMENT,
      dangerouslySetInnerHTML: { __html: dangerouslySetInnerHTML?.__html },
      title,
    };
  };

  setTooltipPosition = (align, placement) => {
    if (!this.tooltip || !this.target || !this.scrollableParent) {
      return;
    }
    let x = 0;
    let y = 0;
    const targetBounds = this.target.getBoundingClientRect();
    const scrollableParentBounds = this.scrollableParent.getBoundingClientRect();

    if (align === 'right') {
      x = this.scrollableParent.scrollLeft - scrollableParentBounds.left + targetBounds.right - this.tooltip.offsetWidth;
    }
    else {
      x = this.scrollableParent.scrollLeft - scrollableParentBounds.left + targetBounds.left;
      if (align === 'center') {
        x = x - (this.tooltip.offsetWidth / 2) + (this.target.offsetWidth / 2);
      }
    }

    y = targetBounds.top
      - scrollableParentBounds.top
      + this.scrollableParent.scrollTop;

    if (placement === 'top') {
      y -= this.tooltip.offsetHeight;
    }
    else if (placement === 'bottom') {
      y += targetBounds.height;
    }

    this.tooltip.style.left = x + 'px';
    this.tooltip.style.top = y + 'px';
    this.tooltip.dataset.placement = placement;
    this.tooltip.dataset.align = align;
  };

  setTooltipContent = (title, dangerouslySetInnerHTML) => {
    if (!this.tooltip) {
      return;
    }
    if (dangerouslySetInnerHTML?.__html) {
      this.tooltip.innerHTML = dangerouslySetInnerHTML?.__html;
    }
    else if (typeof title === 'string') {
      this.tooltip.innerText = title.replace(/\\n/igm, '\n');
    }
    else if (React.isValidElement(title)) {
      ReactDOM.render(title, this.tooltip);
    }
  };

  isTooltipPermanentlyHidden = () => {
    const { overflownOnly } = this.props;
    const isTargetCurrentlyOverflown = this.target.scrollWidth > this.target.offsetWidth;
    let isPermanentlyHidden = false;

    if (overflownOnly && !isTargetCurrentlyOverflown) {
      isPermanentlyHidden = true;
    }
    if (isMobileSizeScreen()) {
      isPermanentlyHidden = true;
    }
    if (isPermanentlyHidden) {
      this.hide();
    }
    return isPermanentlyHidden;
  };

  show = () => {
    const { align, placement } = this.serializeTooltipParams(this.props);
    if (!this.tooltip) {
      this.initialize();
    }
    if (this.tooltip && !this.isTooltipPermanentlyHidden()) {
      this.tooltip.hidden = false;
      this.setTooltipPosition(align, placement);
    }
  };

  hide = () => {
    if (this.tooltip) {
      this.tooltip.hidden = true;
    }
  };

  onTooltipMouseOver = () => {
    this.isMouseOverTooltip = true;
    clearInterval(this.mouseOverTooltipTimeout);

    this.mouseOverTooltipTimeout = setTimeout(() => {
      this.isMouseOverTooltip = false;
    }, 200);
  };

  onTargetMouseEnter = () => {
    const { delay } = this.serializeTooltipParams(this.props);
    clearTimeout(this.showTimeout);
    this.showTimeout = setTimeout(this.show, delay);
  };

  onTargetMouseLeave = () => {
    clearTimeout(this.hideTimeout);
    clearTimeout(this.showTimeout);

    this.hideTimeout = setTimeout(() => {
      if (!this.isMouseOverTooltip) {
        this.hide();
      }
    }, 100);
  };

  render(){
    const {
      title,
      className,
      delay,
      align,
      placement,
      dangerouslySetInnerHTML,
      overflownOnly,
      children,
      ...otherProps
    } = this.props;

    if (children.length) {
      logError('Cannot pass more than one child to <Tooltip>', { props: this.props });
      return null;
    }

    const passedProps = {
      ...otherProps,
      ref: this.targetRef,
      onMouseEnter: this.onTargetMouseEnter,
      onMouseLeave: this.onTargetMouseLeave,
    };
    return React.cloneElement(children, passedProps);
  }
}
